/**
 * Pure cycle counting / physical inventory logic (CC-6: NEVER use
 * Lingui macros here).
 *
 * A count document (inventory_count) groups lines (inventory_count_line)
 * by location. Each line captures THEORETICAL on-hand (theoretical_qty) when
 * added, and the COUNTED quantity by the user (counted_qty). Upon validating
 * the count (draft→done), an ADJUSTMENT stock_move is generated for each line
 * with a discrepancy whose delta (counted − theoretical) reconciles on-hand.
 *
 * Quantities in INTEGERS (minor units, scale factor 1000), same as F1–F3.
 */

export type CountState = "draft" | "done";

export const COUNT_STATES: readonly CountState[] = ["draft", "done"] as const;

export function isCountState(value: string): value is CountState {
  return (COUNT_STATES as readonly string[]).includes(value);
}

export function coerceCountState(value: string | null | undefined): CountState {
  return value && isCountState(value) ? value : "draft";
}

/** Can count be validated? Only from draft. */
export function canValidateCount(state: string | null | undefined): boolean {
  return coerceCountState(state) === "draft";
}

/** Count line (relevant subset for adjustment calculation). */
export interface CountLineLike {
  product_id: string | null;
  lot_id: string | null;
  /** Theoretical on-hand captured when adding the line (minor units). */
  theoretical_qty: number | null;
  /** Quantity counted by user (minor units). */
  counted_qty: number | null;
}

/**
 * Line adjustment delta: counted − theoretical. >0 surplus stock (inbound),
 * <0 shortage stock (outbound), 0 no difference.
 */
export function countLineDelta(line: CountLineLike): number {
  return (line.counted_qty ?? 0) - (line.theoretical_qty ?? 0);
}

/** Adjustment to materialize for a count line with difference. */
export interface CountAdjustment {
  productId: string;
  lotId: string | null;
  /** Delta to apply to on-hand (minor units, != 0). */
  delta: number;
  /** Final on-hand after adjustment = counted quantity. */
  newOnHand: number;
}

/**
 * Plans adjustments for a count: one CountAdjustment for each line whose
 * delta is != 0. Lines without difference (or without product) are ignored (do
 * not generate a move). Deterministic, side-effect free.
 */
export function planCountAdjustments(lines: CountLineLike[]): CountAdjustment[] {
  const adjustments: CountAdjustment[] = [];
  for (const line of lines) {
    const productId = line.product_id;
    if (!productId) {
      continue;
    }
    const delta = countLineDelta(line);
    if (delta === 0) {
      continue;
    }
    adjustments.push({
      productId,
      lotId: line.lot_id ?? null,
      delta,
      newOnHand: line.counted_qty ?? 0,
    });
  }
  return adjustments;
}

/** Total units changing with positive sign (surplus). */
export function countSurplus(lines: CountLineLike[]): number {
  return lines.reduce((acc, l) => acc + Math.max(countLineDelta(l), 0), 0);
}

/** Total missing units (shrinkage) detected by count. */
export function countShortage(lines: CountLineLike[]): number {
  return lines.reduce((acc, l) => acc + Math.max(-countLineDelta(l), 0), 0);
}

// ─── UPCOMING EXPIRY ALERT ─────────────────────────────────────────────────

/** Lot with its expiry (subset for alert). */
export interface ExpiringLotLike {
  id: string;
  name: string | null;
  product_id: string | null;
  expiry_date: string | null;
}

/** Whole days between two ISO dates (YYYY-MM-DD). b − a, rounded down. */
export function daysBetween(a: string, b: string): number {
  const MS_PER_DAY = 86_400_000;
  const ta = Date.parse(a);
  const tb = Date.parse(b);
  if (Number.isNaN(ta) || Number.isNaN(tb)) {
    return Number.NaN;
  }
  return Math.floor((tb - ta) / MS_PER_DAY);
}

/** Lot approaching expiry (or already expired) with remaining days. */
export interface ExpiryAlert {
  lot: ExpiringLotLike;
  /** Days until expiry (negative = already expired). */
  daysToExpiry: number;
  /** Is it already expired as of `today`? */
  expired: boolean;
}

/**
 * Calculates upcoming expiry alerts: lots whose expiry date falls
 * within the next `withinDays` days (or already expired). Ignores lots without
 * date. Sorts by earliest expiry first (expired at the top).
 * Deterministic: `today` is passed as ISO (YYYY-MM-DD) to avoid clock dependency.
 */
export function computeExpiryAlerts(
  lots: ExpiringLotLike[],
  today: string,
  withinDays: number,
): ExpiryAlert[] {
  const alerts: ExpiryAlert[] = [];
  for (const lot of lots) {
    if (!lot.expiry_date) {
      continue;
    }
    const days = daysBetween(today, lot.expiry_date);
    if (Number.isNaN(days)) {
      continue;
    }
    if (days <= withinDays) {
      alerts.push({ lot, daysToExpiry: days, expired: days < 0 });
    }
  }
  return alerts.toSorted((a, b) => a.daysToExpiry - b.daysToExpiry);
}
